export const formatDateWithTimezone = (
  source: string,
): { date: string; time: string; timezone: string } => {
  const year = source.slice(0, 4);
  const month = source.slice(5, 7);
  const day = source.slice(8, 10);
  const hour = source.slice(11, 13);
  const minute = source.slice(14, 16);
  const timezone = source.slice(-6);

  return {
    date: `${day}/${month}/${year}`,
    time: `${hour}:${minute}`,
    timezone: timezone,
  };
};

/** Opening-day indices: 0 = Monday … 4 = Friday */
const WEEKDAY_THREE_LETTERS_FR = ["Lun", "Mar", "Mer", "Jeu", "Ven"] as const;

type OpeningHoursSlot = { open: string; close: string };
export type OpeningHours = Record<string, OpeningHoursSlot[]>;

/**
 * Format opening hours to display days with abbreviations
 * Example: "Lun, Mar, Mer" or "Lun - Ven"
 */
export function formatOpeningHoursDays(openingHours: OpeningHours | undefined): string {
  if (!openingHours) return "";

  const days = Object.entries(openingHours)
    .filter(([, slots]) => slots && slots.length > 0)
    .map(([day]) => Number.parseInt(day, 10))
    .filter((n) => Number.isInteger(n) && n >= 0 && n <= 4) // Only weekdays
    .sort((a, b) => a - b);

  if (days.length === 0) return "";
  return days.map((d) => WEEKDAY_THREE_LETTERS_FR[d]).join(", ");
}

/**
 * Format opening hours slots for a summary display
 * Example: "08:00 - 12:00 et 14:00 - 18:00"
 */
export function formatOpeningHoursSummary(openingHours: OpeningHours | undefined): string {
  if (!openingHours) return "";

  // Get the first day with slots to show as representative
  const firstDayWithSlots = Object.entries(openingHours)
    .filter(([, slots]) => slots && slots.length > 0)
    .sort(([a], [b]) => Number(a) - Number(b))[0];

  if (!firstDayWithSlots) return "";

  const slots = firstDayWithSlots[1];
  return slots
    .filter((slot) => slot.open && slot.close)
    .map((slot) => `${slot.open} - ${slot.close}`)
    .join(" et ");
}

const formatOpeningHoursSlots = (slots: OpeningHoursSlot[]): string =>
  slots
    .filter((slot) => slot.open && slot.close)
    .map((slot) => `${slot.open} - ${slot.close}`)
    .join(" et ");

/**
 * Combined format for pickup opening hours display, grouping consecutive days
 * that share the exact same hours and splitting out days that differ.
 * Example: "Lun, Mar : 08:00 - 18:00 / Jeu : 09:00 - 10:00"
 */
export function formatOpeningHoursDisplay(openingHours: OpeningHours | undefined): string {
  if (!openingHours) return "";

  const daysWithSlots = Object.entries(openingHours)
    .filter(([, slots]) => slots && slots.length > 0)
    .map(([day, slots]) => [Number.parseInt(day, 10), slots] as const)
    .filter(([day]) => Number.isInteger(day) && day >= 0 && day <= 4)
    .sort(([a], [b]) => a - b);

  if (daysWithSlots.length === 0) return "";

  const groups: { hours: string; days: number[] }[] = [];
  for (const [day, slots] of daysWithSlots) {
    const hours = formatOpeningHoursSlots(slots);
    const group = groups.find((g) => g.hours === hours);
    if (group) {
      group.days.push(day);
    } else {
      groups.push({ hours, days: [day] });
    }
  }

  return groups
    .map(
      (group) =>
        `${group.days.map((d) => WEEKDAY_THREE_LETTERS_FR[d]).join(", ")} : ${group.hours}`,
    )
    .join(" / ");
}
